import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

import Currency from '@/components/atoms/Currency/Currency';
import { InnerContent } from '@/components/atoms/InnerContent/InnerContent';

import CareProviderProfileSection from '@/components/molecules/CareProviderProfileSection/CareProviderProfileSection';

const FundingInfoDefault = ({
  country,
  value,
}: {
  country: string;
  value: number;
}) => (
  <CareProviderProfileSection title={`Funding for care in ${country}`}>
    <InnerContent
      items={[
        {
          description: (
            <>
              If your total savings and property assets are below{' '}
              <strong>
                <Currency value={value} />
              </strong>
              , then you may be able to access local authority funding towards{' '}
              <strong>accommodation</strong> costs,{' '}
              <strong>personal care</strong> and <strong>nursing care</strong>.
              <br />
              <br />
              To qualify, you must first arrange a care needs assessment.
            </>
          ),
        },
      ]}
    />
  </CareProviderProfileSection>
);

const FundingInfoCqc = () => (
  <FundingInfoDefault country='England' value={23250} />
);
const FundingInfoRqia = () => (
  <FundingInfoDefault country='Northern Ireland' value={23250} />
);
const FundingInfoCiWales = () => (
  <FundingInfoDefault country='Wales' value={50000} />
);

const FundingInfoCi = () => {
  return (
    <CareProviderProfileSection title={`Funding for care in Scotland`}>
      <InnerContent
        items={[
          {
            description:
              'Your care home costs could be partially funded by the council. To qualify for this, you must first arrange a care needs assessment.',
          },
          {
            icon: 'check',
            iconColor: '#51ac7c',
            description: (
              <>
                <strong>Free personal and/or nursing care funding</strong> is
                available to anyone aged 65 or over, regardless of their
                financial situation.
              </>
            ),
          },
          {
            icon: 'check',
            iconColor: '#51ac7c',
            description: (
              <>
                Funding towards <strong>accommodation</strong> costs is also
                available if your assets (property &amp; savings) are below{' '}
                <strong>
                  <Currency value={27250} />
                </strong>
              </>
            ),
          },
        ]}
      />
    </CareProviderProfileSection>
  );
};

const fundingInfo = {
  ci: FundingInfoCi,
  cqc: FundingInfoCqc,
  rqia: FundingInfoRqia,
  ci_wales: FundingInfoCiWales,
};

const PriceKnown = ({ pricePerWeek }: { pricePerWeek: number }) => (
  <InnerContent
    items={[
      {
        description: (
          <>
            Prices for this home start at{' '}
            <strong>
              <Currency value={pricePerWeek} />
            </strong>{' '}
            per week. The final price will depend on your individual
            requirements -{' '}
            <strong>contact the care home for more details.</strong>
          </>
        ),
      },
    ]}
  />
);

const PriceUnknown = () => (
  <InnerContent
    items={[
      {
        description: (
          <>
            Sorry, we don&apos;t have prices for this care home - please{' '}
            <strong>contact the care home</strong> for more details.
          </>
        ),
      },
    ]}
  />
);

interface CareHomeFundingProps {
  regulator?: 'ci' | 'cqc' | 'rqia' | 'ci_wales';
  pricePerWeek: number;
}

export const CareHomeFunding = ({
  regulator,
  pricePerWeek,
}: CareHomeFundingProps) => {
  const FundingInfo = regulator ? fundingInfo[regulator] : undefined;
  const Price = pricePerWeek ? PriceKnown : PriceUnknown;

  return (
    <CareProviderProfileSection>
      <Stack
        sx={{
          gap: sp[32],
          typography: 'bodyMd',
          justifyContent: 'stretch',
          flexDirection: 'column',
          '@media (min-width: 960px)': { flexDirection: 'row' },
        }}
      >
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <CareProviderProfileSection title={`Prices`} isDivider={false}>
            <Price pricePerWeek={pricePerWeek} />
          </CareProviderProfileSection>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          {FundingInfo && <FundingInfo />}
        </Box>
      </Stack>
    </CareProviderProfileSection>
  );
};

export default CareHomeFunding;
